import React, { FunctionComponent, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { BrowserRouter as Router, Route, Routes } from "react-router";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";

import SignIn from "./auth/SignIn";
import Registrations from "./registration/Registrations";
import UpdateRegistrationEditor from "./registration/UpdateRegistrationEditor";
import Limits from "./limit/Limits";
import Navbar from "./common/navbar/Navbar";
import UpdateLimitEditor from "./limit/UpdateLimitEditor";
import Dashboard from "./dashboard/Dashboard";
import Admin from "./admin/Admin";
import { Settings } from "./settings/Settings";
import CreateRegistrationEditor from "./registration/CreateRegistrationEditor";
import CreateLimitEditor from "./limit/CreateLimitEditor";
import { useAuthUser } from "../hooks/useAuthUser";
import { firebaseAppAuth } from "../lib/firebase";
import { useAuthAdmin } from "../hooks/useAuthAdmin";
import { GlobalErrorBoundary } from "../core/GlobalErrorBoundary";
import SignUp from "./auth/SignUp";
import { Form } from "./form/Form";
import { Email } from "./email/Email";
import { SpielgruppePlus } from "./premium/SpielgruppePlus";
import { Footer } from "./common/footer/Footer";
import { Datenschutz } from "./staticsites/Datenschutz";
import { Agb } from "./staticsites/Agb";
import { PublicRegistrationForm } from "./registrationform/PublicRegistrationForm";
import ResetPassword from "./auth/ResetPassword";
import { identifyUser, resetUser } from "../helper/analytics";
import { useUserContext } from "../contexts/UserContext";
import UserManagement from "./admin/UserManagement";
import { usePageTracking } from "../hooks/usePageTracking";

const App: FunctionComponent = () => {
  const { user, loading: authLoading } = useAuthUser();
  const { activeUser } = useUserContext();
  const isAuthenticated = !!user;
  const isAdmin = useAuthAdmin(user);

  usePageTracking();

  useEffect(() => {
    if (user && activeUser) {
      identifyUser(user.uid, {
        email: user.email,
        displayName: user.displayName,
        isAdmin,
        userId: activeUser.id,
        domain: import.meta.env.VITE_PUBLIC_DOMAIN
      });
    }
  }, [user, activeUser, isAdmin]);

  if (authLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Laden...</span>
        </div>
      </div>
    );
  }

  // Handle user logout
  const handleSignOut = async (): Promise<void> => {
    resetUser();
    return firebaseAppAuth.signOut();
  };

  return (
    <div className={"h-100"}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Webtie Anmeldungen</title>
        <meta name="description" content="Anmeldungen online verwalten" />
      </Helmet>
      <GlobalErrorBoundary>
        <>
          <Router>
            {user && <Navbar userId={activeUser?.id || user.uid} isAdmin={isAdmin} signOut={() => handleSignOut()} />}
            <Routes>
              <Route path="/user/:userId/dashboard" element={<Dashboard isAuthenticated={isAuthenticated} />} />
              <Route
                path="/user/:userId/registrations/create"
                element={<CreateRegistrationEditor isAuthenticated={isAuthenticated} />}
              />
              <Route
                path="/user/:userId/registrations/:registrationId"
                element={<UpdateRegistrationEditor isAuthenticated={isAuthenticated} />}
              />
              <Route
                path="/user/:userId/limits/create"
                element={<CreateLimitEditor isAuthenticated={isAuthenticated} />}
              />
              <Route
                path="/user/:userId/limits/:limitId"
                element={<UpdateLimitEditor isAuthenticated={isAuthenticated} />}
              />
              <Route path="/user/:userId/limits" element={<Limits isAuthenticated={isAuthenticated} />} />
              <Route path="/user/:userId/form" element={<Form isAuthenticated={isAuthenticated} />} />
              <Route path="/user/:userId/email" element={<Email isAuthenticated={isAuthenticated} />} />
              <Route path="/user/:userId/registrations" element={<Registrations isAuthenticated={isAuthenticated} />} />
              <Route path="/user/:userId/admin" element={<Admin isAuthenticated={isAuthenticated} />} />
              <Route
                path="/user/:userId/user-management"
                element={<UserManagement isAuthenticated={isAuthenticated} />}
              />
              <Route
                path="/user/:userId/settings"
                element={<Settings isAuthenticated={isAuthenticated} isAdmin={isAdmin} />}
              />
              <Route path="/user/:userId/plus" element={<SpielgruppePlus isAuthenticated={isAuthenticated} />} />
              <Route path="/signup" element={<SignUp user={user} />} />
              <Route path="/reset-password" element={<ResetPassword user={user} />} />
              <Route path="/formular/:userId" element={<PublicRegistrationForm />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<Agb />} />
              <Route path="/" element={<SignIn user={user} />} />
            </Routes>
            <Footer userId={activeUser?.id || user?.uid} />
          </Router>
        </>
      </GlobalErrorBoundary>
      <ToastContainer />
    </div>
  );
};

export default App;
